import { Button } from "@awtoban/ui";
import { Link, useNavigate } from "@tanstack/react-router";
import { LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../features/auth/AuthProvider";
import type { Locale } from "../i18n";
import { LocaleSwitcher } from "../i18n/LocaleSwitcher";

export function bcp47(locale: Locale): string {
  if (locale === "en") return "en-US";
  if (locale === "ru") return "ru-RU";
  return "tk-TM";
}

type Section = "dashboard" | "operators" | "companies" | "integrity" | "pricing";

const LINKS: { section: Section; to: string; key: string }[] = [
  { section: "dashboard", to: "/", key: "nav.dashboard" },
  { section: "operators", to: "/operators", key: "nav.operators" },
  { section: "companies", to: "/companies", key: "nav.companies" },
  { section: "integrity", to: "/integrity", key: "nav.integrity" },
  { section: "pricing", to: "/pricing/compare", key: "nav.pricing" },
];

/** Shared HQ top bar: section nav + user + locale + logout. */
export function HqHeader({ active }: { active: Section }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();

  return (
    <header className="flex flex-wrap items-center gap-x-6 gap-y-2 border-line-strong border-b bg-paper px-4 py-3 sm:px-6">
      <nav className="flex items-center gap-5">
        {LINKS.map((l) =>
          l.section === active ? (
            <span key={l.section} className="font-medium text-sm">
              {t(l.key)}
            </span>
          ) : (
            <Link key={l.section} to={l.to} className="text-ink-muted text-sm hover:text-ink">
              {t(l.key)}
            </Link>
          ),
        )}
      </nav>
      <div className="ml-auto flex flex-wrap items-center gap-4 text-sm sm:gap-6">
        <div className="font-medium">{auth.user?.username}</div>
        <LocaleSwitcher />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            auth.logout();
            void navigate({ to: "/login" });
          }}
        >
          <LogOut className="h-4 w-4" />
          {t("auth.log_out")}
        </Button>
      </div>
    </header>
  );
}
